import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { StepFlow } from '@/components/admin/StepFlow';
import { createMember } from '@/features/members/api';
import {
  InvitationMailConfirmModal,
  type InvitationSendResult,
} from '@/features/invitations/InvitationMailConfirmModal';
import { EMPTY_MEMBER_FORM, MemberFormFields, type MemberFormValues } from '@/features/members/MemberFormFields';
import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';

/** 会員の新規登録（事務局のみ / 指示書 §12 M3）. M2 のメタボックスを使い回す。 */
export function MemberCreatePage(): ReactElement {
  const navigate = useNavigate();
  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });
  const [values, setValues] = useState<MemberFormValues>(EMPTY_MEMBER_FORM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 送る前に内容を確認してもらう（D-153）
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendResult, setSendResult] = useState<InvitationSendResult | null>(null);
  const [createdMemberId, setCreatedMemberId] = useState<string | null>(null);

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const member = await createMember({
        name: values.name,
        name_kana: values.name_kana || null,
        company: values.company || null,
        position: values.position || null,
        specialty: values.specialty || null,
        invite_email: values.invite_email || null,
        cohort_id: values.cohort_id,
        course_id: values.course_id,
        bio: values.bio || null,
        company_url: values.company_url || null,
        subordinate_count:
          values.subordinate_count === '' ? null : Number(values.subordinate_count),
        years_of_service: values.years_of_service === '' ? null : Number(values.years_of_service),
        hobbies: values.hobbies || null,
        career_history: values.career_history || null,
        participation_purpose: values.participation_purpose || null,
        strengths: values.strengths || null,
        weaknesses: values.weaknesses || null,
        goals: values.goals || null,
        phone: values.phone || null,
        contact_email: values.contact_email || null,
        tags: values.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      // 受諾リンクはこの応答にしか無いので、閉じる前に結果として見せる（D-153）
      setCreatedMemberId(member.id);
      setSendResult({
        acceptUrl: member.invitation_accept_url,
        mailSent: member.invitation_mail_sent,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader title="会員の新規登録" />
      <StepFlow
        steps={[
          {
            label: '基本情報を登録する（氏名・メールアドレス・期・コースは必須）',
            detail: '会員No.は自動で採番されます。期とコースはここで必ず選びます。会社など他の項目は空のままでも構いません',
          },
          {
            label: 'ご本人がプロフィールを入力する',
            detail: '届いたリンクからパスワードを決め、空いている項目を本人が埋めます',
          },
          { label: '期・コースの変更', detail: '所属の変更は事務局が会員のプロフィール編集から行います' },
        ]}
        currentIndex={0}
        note="人数が多いときは、この画面ではなく「会員CSV取込」でまとめて登録できます。"
      />
      <MetaBox title="基本情報">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}
        {cohorts.isError && (
          <Notice kind="error" dismissible={false}>
            期マスタの取得に失敗しました。
            <button type="button" onClick={() => void cohorts.refetch()} className="ml-2 underline">
              再試行
            </button>
          </Notice>
        )}
        <MemberFormFields
          values={values}
          onChange={setValues}
          cohorts={cohorts.data ?? []}
          courses={courses.data ?? []}
          memberIdEditable
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={
              submitting ||
              !values.name ||
              !values.invite_email ||
              !values.cohort_id ||
              !values.course_id
            }
            onClick={() => setConfirmOpen(true)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '招待中…' : '招待する'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>

      <InvitationMailConfirmModal
        open={confirmOpen}
        email={values.invite_email}
        busy={submitting}
        result={sendResult}
        onSend={() => void onSubmit()}
        onCancel={() => setConfirmOpen(false)}
        onDone={() => {
          setConfirmOpen(false);
          navigate(createdMemberId ? `/members/${createdMemberId}` : '/members');
        }}
      />
    </>
  );
}
